import React from 'react';
import { useColorName } from '../../../hooks/useColorName.js';

export default function ColorNameFinder({ initialColor = '#3B82F6' }) {
  const { hex, setHex, isValid, best, alternatives } = useColorName(initialColor);

  const handleTextChange = (e) => {
    const value = e.target.value;
    setHex(value.startsWith('#') ? value : `#${value}`);
  };

  return (
    <div style={styles.card}>
      <h2 style={styles.heading}>Color Name Finder</h2>

      <div style={styles.inputRow}>
        <input
          type="color"
          value={isValid ? hex : '#000000'}
          onChange={(e) => setHex(e.target.value)}
          style={styles.colorInput}
          aria-label="Pick a color"
        />
        <input
          type="text"
          value={hex}
          onChange={handleTextChange}
          placeholder="#3B82F6"
          style={{
            ...styles.textInput,
            borderColor: isValid ? '#D1D5DB' : '#EF4444',
          }}
          aria-label="Hex color value"
        />
      </div>

      {!isValid && <p style={styles.error}>Enter a valid hex color, e.g. #3B82F6 or #3BF.</p>}

      {isValid && best && (
        <>
          <div style={styles.bestMatch}>
            <div style={{ ...styles.swatchLarge, background: hex }} />
            <div>
              <div style={styles.matchName}>{best.name}</div>
              <div style={styles.matchHex}>{best.hex}</div>
              <span style={best.isExactMatch ? styles.badgeExact : styles.badgeClose}>
                {best.isExactMatch ? 'Exact match' : `${best.similarity}% match`}
              </span>
            </div>
          </div>

          {alternatives.length > 0 && !best.isExactMatch && (
            <div style={styles.altSection}>
              <div style={styles.altLabel}>Also close:</div>
              <div style={styles.altList}>
                {alternatives.map((alt) => (
                  <button
                    key={alt.name}
                    type="button"
                    onClick={() => setHex(alt.hex)}
                    style={styles.altChip}
                    title={`${alt.similarity}% match`}
                  >
                    <span style={{ ...styles.swatchSmall, background: alt.hex }} />
                    {alt.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const styles = {
  card: {
    fontFamily: 'system-ui, -apple-system, sans-serif',
    maxWidth: 380,
    padding: 20,
    borderRadius: 12,
    border: '1px solid #E5E7EB',
    background: '#FFFFFF',
  },
  heading: { margin: '0 0 16px', fontSize: 16, fontWeight: 600, color: '#111827' },
  inputRow: { display: 'flex', gap: 8, marginBottom: 8 },
  colorInput: {
    width: 44,
    height: 40,
    padding: 0,
    border: '1px solid #D1D5DB',
    borderRadius: 8,
    cursor: 'pointer',
  },
  textInput: {
    flex: 1,
    height: 40,
    padding: '0 12px',
    border: '1px solid #D1D5DB',
    borderRadius: 8,
    fontSize: 14,
  },
  error: { color: '#EF4444', fontSize: 13, margin: '4px 0 0' },
  bestMatch: { display: 'flex', alignItems: 'center', gap: 14, marginTop: 16 },
  swatchLarge: {
    width: 64,
    height: 64,
    borderRadius: 10,
    border: '1px solid rgba(0,0,0,0.08)',
    flexShrink: 0,
  },
  matchName: { fontSize: 18, fontWeight: 600, color: '#111827' },
  matchHex: { fontSize: 13, color: '#6B7280', marginTop: 2, fontFamily: 'monospace' },
  badgeExact: {
    display: 'inline-block',
    marginTop: 6,
    padding: '2px 8px',
    borderRadius: 999,
    background: '#DCFCE7',
    color: '#166534',
    fontSize: 12,
    fontWeight: 500,
  },
  badgeClose: {
    display: 'inline-block',
    marginTop: 6,
    padding: '2px 8px',
    borderRadius: 999,
    background: '#FEF3C7',
    color: '#92400E',
    fontSize: 12,
    fontWeight: 500,
  },
  altSection: { marginTop: 18, paddingTop: 14, borderTop: '1px solid #F3F4F6' },
  altLabel: { fontSize: 12, color: '#6B7280', marginBottom: 8 },
  altList: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  altChip: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 10px 4px 4px',
    borderRadius: 999,
    border: '1px solid #E5E7EB',
    background: '#F9FAFB',
    fontSize: 13,
    cursor: 'pointer',
  },
  swatchSmall: { width: 18, height: 18, borderRadius: '50%', border: '1px solid rgba(0,0,0,0.08)' },
};
